import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  MessageCircle, Send, Loader2, Users, ImageIcon, CheckCircle2, AlertTriangle, X,
} from "lucide-react";

const buildMessage = (label) =>
  `Seu documento *${label}* está disponível para assinatura, acesse o aplicativo da AVI Segurança para assinar.`;

/**
 * Confirmation dialog shown right after the admin creates one of the
 * signable document types (Aviso de Férias, Recibo de Férias, Kit
 * Admissional). Asks "DESEJA INFORMAR AO COLABORADOR POR WHATSAPP?",
 * fires /api/admin/broadcast-whatsapp filtered to the supplied
 * recipients and then KEEPS the dialog open polling the job status so
 * the admin sees a live progress bar (same UX as the bulk WhatsApp
 * broadcast).
 *
 * Props:
 *   open                 — visibility
 *   onOpenChange         — close handler
 *   documentLabel        — string ("Aviso de Férias", etc.) used in
 *                          the message body
 *   recipients           — array of {employee_id, nome, matricula}
 *                          produced by the upload response
 */
const NotifyDocumentWhatsAppDialog = ({
  open,
  onOpenChange,
  documentLabel,
  documentType,
  recipients,
}) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [sending, setSending] = useState(false);
  const [includeLogo, setIncludeLogo] = useState(true);
  const [eligibleCount, setEligibleCount] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  // Ready-made templates linked to this document type (kit-admissional,
  // aviso-ferias, recibo-ferias). When a template is selected, its
  // title/body/video replace the default message; otherwise we fall
  // back to the built-in line.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const ids = (recipients || [])
    .map((r) => r.employee_id || r.id)
    .filter(Boolean);

  // Reset transient state every time the dialog opens
  useEffect(() => {
    if (!open) return;
    setJob(null);
    setSending(false);
    setEligibleCount(null);
    setIncludeLogo(true);
    setSelectedTemplate(null);
    setTemplates([]);
  }, [open]);

  // Load templates linked to this document type
  useEffect(() => {
    if (!open || !documentType) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/message-templates/for-document/${documentType}`,
          { headers, timeout: 10000 },
        );
        if (cancelled) return;
        const items = r.data?.items || [];
        setTemplates(items);
        // Pre-select the first template with video (preferred default)
        // — otherwise pick the first template; admin can switch to
        // "Mensagem padrão da AVI" if they want a text-only message.
        const firstWithVideo = items.find((t) => t.video_id);
        if (firstWithVideo) {
          setSelectedTemplate(firstWithVideo);
        } else if (items.length > 0) {
          setSelectedTemplate(items[0]);
        }
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentType]);

  // Ask the backend how many of those are actually confirmed-whatsapp
  useEffect(() => {
    if (!open || ids.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/admin/broadcast-whatsapp/eligible`,
          { headers, timeout: 15000 },
        );
        const eligibleSet = new Set((r.data.employees || []).map((e) => e.id));
        const count = ids.filter((id) => eligibleSet.has(id)).length;
        if (!cancelled) setEligibleCount(count);
      } catch {
        if (!cancelled) setEligibleCount(0);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recipients]);

  // Poll the broadcast job status while it's running
  useEffect(() => {
    if (!job?.job_id) return undefined;
    if (job.status === "done") {
      clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/admin/broadcast-whatsapp/${job.job_id}`,
          { headers, timeout: 10000 },
        );
        setJob(r.data);
        if (r.data.status === "done") clearInterval(pollRef.current);
      } catch { /* transient */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.job_id, job?.status]);

  const message = buildMessage(documentLabel);

  const handleConfirm = async () => {
    if (eligibleCount === 0) {
      toast.error("Nenhum funcionário com WhatsApp confirmado.");
      return;
    }
    setSending(true);
    try {
      let r;
      if (selectedTemplate) {
        // Use the template's WhatsApp broadcast endpoint (handles
        // attaching the video natively when the template has one).
        r = await axios.post(
          `${API}/admin/message-templates/${selectedTemplate.id}/broadcast`,
          { employee_ids: ids, include_logo: includeLogo },
          { headers, timeout: 15000 },
        );
      } else {
        r = await axios.post(
          `${API}/admin/broadcast-whatsapp`,
          { message, include_logo: includeLogo, employee_ids: ids },
          { headers, timeout: 15000 },
        );
      }
      toast.success(
        `WhatsApp será enviado a ${r.data.recipients} funcionário(s) em background.`,
      );
      setJob({
        job_id: r.data.job_id,
        status: "queued",
        sent: 0, failed: 0,
        total: r.data.recipients,
        errors: [],
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar.");
    } finally {
      setSending(false);
    }
  };

  const isRunning = job && job.status !== "done";
  const isDone = job && job.status === "done";
  const progress = job && job.total
    ? Math.min(100, Math.round(((job.sent + job.failed) / job.total) * 100))
    : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Block closing while a broadcast job is actively running so
        // the admin doesn't lose track of progress (still allowed when
        // the job is done or before it has even started).
        if (!o && isRunning) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="notify-document-whatsapp-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            {job
              ? (isDone ? "Disparo finalizado" : "Enviando WhatsApp…")
              : "Deseja informar ao colaborador por WhatsApp?"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {job
              ? "Acompanhe o progresso abaixo. Você pode fechar este aviso quando o disparo terminar."
              : "Envie um aviso instantâneo via WhatsApp para que os colaboradores saibam que há documento aguardando assinatura."}
          </DialogDescription>
        </DialogHeader>

        {/* Pre-send section: only shown before firing the job */}
        {!job && (
          <div className="space-y-3 py-2">
            {/* Recipient summary */}
            <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-md border border-slate-200">
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <Users className="w-4 h-4 text-slate-500" />
                <span>
                  {ids.length} funcionário(s) no lote
                </span>
              </div>
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]"
                data-testid="notify-eligible-count"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {eligibleCount === null ? "..." : eligibleCount} com WhatsApp
              </Badge>
            </div>

            {/* Ready-made templates for this document type — video
                templates ranked first; "Mensagem padrão da AVI" lives
                at the bottom as the fallback option. */}
            {templates.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">
                  Mensagens prontas para {documentLabel}
                </p>
                <div className="space-y-1.5">
                  {[...templates]
                    .sort((a, b) => (b.video_id ? 1 : 0) - (a.video_id ? 1 : 0))
                    .map((t) => {
                    const selected = selectedTemplate?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setSelectedTemplate(t)}
                        className={`w-full text-left px-2.5 py-2 rounded-md border transition ${
                          selected
                            ? "border-emerald-600 bg-emerald-50 border-2"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                        data-testid={`notify-template-${t.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-slate-800 truncate flex-1">
                            {t.title}
                          </p>
                          {t.video_id && (
                            <Badge variant="outline" className="bg-emerald-600 text-white border-emerald-600 text-[10px] flex-shrink-0">
                              vídeo
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">
                          {t.body}
                        </p>
                      </button>
                    );
                  })}
                  {/* Visual separator before the plain-text fallback */}
                  <div className="flex items-center gap-2 my-1.5">
                    <div className="h-px bg-slate-200 flex-1" />
                    <span className="text-[10px] uppercase tracking-wide text-slate-400">
                      ou
                    </span>
                    <div className="h-px bg-slate-200 flex-1" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedTemplate(null)}
                    className={`w-full text-left px-2.5 py-2 rounded-md border transition ${
                      !selectedTemplate
                        ? "border-emerald-600 bg-emerald-50 border-2"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid="notify-template-default"
                  >
                    <p className="text-xs font-medium text-slate-800">
                      Mensagem padrão da AVI
                      <span className="ml-1 text-[10px] text-slate-400 font-normal">
                        (sem vídeo)
                      </span>
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Texto fixo informando que o {documentLabel.toLowerCase()} está pronto.
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Message preview */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">
                Mensagem que será enviada
              </p>
              <motion.div
                key={selectedTemplate?.id || "default"}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-emerald-50/60 border border-emerald-200 rounded-md text-sm text-slate-800 leading-relaxed whitespace-pre-wrap"
                data-testid="notify-message-preview"
              >
                {selectedTemplate
                  ? `*${selectedTemplate.title}*\n\n${selectedTemplate.body}`
                  : message}
                {selectedTemplate?.video_id && (
                  <p className="mt-2 text-[11px] text-slate-500 italic">
                    + Vídeo anexo: {selectedTemplate.video_filename || "vídeo"}
                  </p>
                )}
              </motion.div>
            </div>

            <label className="flex items-center space-x-2 cursor-pointer text-xs text-slate-700">
              <input
                type="checkbox"
                checked={includeLogo}
                onChange={(e) => setIncludeLogo(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                disabled={sending}
                data-testid="notify-include-logo"
              />
              <ImageIcon className="w-3.5 h-3.5 text-slate-500" />
              <span>Incluir logo da AVI Segurança</span>
            </label>

            {eligibleCount === 0 && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  Nenhum dos funcionários deste lote tem WhatsApp confirmado.
                  Eles ainda receberão o aviso assim que confirmarem o
                  número no primeiro acesso.
                </span>
              </div>
            )}
          </div>
        )}

        {/* Live progress panel: shown while/after the job runs */}
        {job && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3 py-2"
            data-testid="notify-progress-panel"
          >
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-slate-700">
                {isDone ? "Finalizado" : "Em andamento"}
              </span>
              <Badge
                variant="outline"
                className={
                  isDone
                    ? "bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]"
                    : "bg-amber-100 text-amber-800 border-amber-200 text-[10px]"
                }
                data-testid="notify-progress-count"
              >
                {isDone ? (
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                ) : (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                )}
                {job.sent + job.failed}/{job.total || 0}
              </Badge>
            </div>

            <Progress value={progress} className="h-2.5" />

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 bg-emerald-50 rounded text-center">
                <p
                  className="text-lg font-semibold text-emerald-700"
                  data-testid="notify-sent"
                >
                  {job.sent}
                </p>
                <p className="text-[10px] text-emerald-600 uppercase tracking-wide">
                  Recebidas
                </p>
              </div>
              <div className="p-2 bg-red-50 rounded text-center">
                <p
                  className="text-lg font-semibold text-red-700"
                  data-testid="notify-failed"
                >
                  {job.failed}
                </p>
                <p className="text-[10px] text-red-600 uppercase tracking-wide">
                  Falhas
                </p>
              </div>
            </div>

            {job.errors && job.errors.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-red-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Detalhes dos erros ({job.errors.length})
                </summary>
                <ul className="mt-1 max-h-32 overflow-y-auto space-y-1 pl-1">
                  {job.errors.map((e, i) => (
                    <li key={i} className="text-[10px] text-red-600">
                      <span className="font-mono">{e.matricula}</span>{" "}
                      {e.nome}: {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </motion.div>
        )}

        <DialogFooter className="gap-2">
          {!job && (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={sending}
                data-testid="notify-skip-btn"
              >
                <X className="w-4 h-4 mr-1.5" />
                Não, pular
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={sending || eligibleCount === 0 || eligibleCount === null}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="notify-confirm-btn"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-1.5" />
                )}
                Sim, enviar WhatsApp
              </Button>
            </>
          )}
          {job && (
            <Button
              onClick={() => onOpenChange(false)}
              disabled={isRunning}
              className={isDone ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              data-testid="notify-close-btn"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Aguarde…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Fechar
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotifyDocumentWhatsAppDialog;
